import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { prodApi, num, fmtKg, fmtPct, today, outKey, errMsg, SELL } from "./prodApi";
import { useProd } from "./ProdApp";
import { PButton, PInput, PNum, PSelect, PField, PTitle, PCard, PModal, ReconcileBar, reconcileStatus, PBadge, Spinner, PEmpty } from "./ProdUi";

const S = (v) => (v == null ? "" : String(v).replace(".", ","));

export default function ProdQuickYield() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { recipes, nodes, trims, user } = useProd();
  const runId = params.get("run");
  const [recipe, setRecipe] = useState(null);
  const [inp, setInp] = useState({ total_kg: "", pieces: "1", price_per_kg: "", supplier_name: "", origin: "", lot: "" });
  const [meta, setMeta] = useState({ date: today(), operator: user?.name || "", notes: "" });
  const [weights, setWeights] = useState({});
  const [extra, setExtra] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addSel, setAddSel] = useState({ node_id: "", trim_level_id: "" });
  const [busy, setBusy] = useState(false);
  const [loadingRun, setLoadingRun] = useState(!!runId);

  useEffect(() => {
    const rid = params.get("recipe");
    if (runId) {
      prodApi.get(`/production/runs/${runId}`).then((r) => {
        const run = r.data;
        setRecipe(run.recipe_snapshot);
        setInp({ total_kg: S(run.input?.total_kg), pieces: S(run.input?.pieces ?? 1), price_per_kg: S(run.input?.price_per_kg), supplier_name: run.input?.supplier_name || "", origin: run.input?.origin || "", lot: run.input?.lot || "" });
        setMeta({ date: run.date, operator: run.operator || "", notes: run.notes || "" });
        setWeights(Object.fromEntries(run.outputs.map((o) => [outKey(o), S(o.weight_kg)])));
        setExtra(run.recipe_snapshot.outputs.filter((o) => o.extra));
      }).catch((e) => { toast.error(errMsg(e)); navigate("/produzione/lavorazioni"); }).finally(() => setLoadingRun(false));
    } else if (rid) {
      prodApi.get(`/production/recipes/${rid}`).then((r) => setRecipe(r.data)).catch(() => setParams({}));
    } else setRecipe(null);
  }, [params, runId, navigate, setParams]);

  const rows = useMemo(() => {
    if (!recipe) return [];
    const base = recipe.outputs.filter((o) => !o.extra);
    return [...base, ...extra];
  }, [recipe, extra]);

  const inputKg = num(inp.total_kg);
  const total = rows.reduce((s, o) => s + num(weights[outKey(o)]), 0);
  const st = reconcileStatus(inputKg, total);

  const addExtra = () => {
    const n = nodes.find((x) => x.id === addSel.node_id);
    if (!n) return;
    const t = trims.find((x) => x.id === addSel.trim_level_id);
    const row = { node_id: n.id, node_name: n.name, trim_level_id: t?.id || null, trim_level_name: t?.name || null, pct: 0, disposal: n.disposal, extra: true };
    if (rows.some((o) => outKey(o) === outKey(row))) { toast.error("Taglio già presente"); return; }
    setExtra((e) => [...e, row]);
    setAddOpen(false);
  };

  const save = async () => {
    if (inputKg <= 0) { toast.error("Inserisci il peso in ingresso"); return; }
    if (total <= 0) { toast.error("Registra almeno un peso in uscita"); return; }
    if (st.status !== "ok" && !window.confirm(`Differenza da riconciliare: ${fmtKg(st.diff, 3)} kg. Salvare comunque? La differenza verrà registrata come calo / non riconciliato.`)) return;
    setBusy(true);
    const body = {
      recipe_id: recipe.id, date: meta.date, operator: meta.operator, notes: meta.notes, method: "weighted",
      input: { total_kg: inputKg, pieces: num(inp.pieces) || 1, price_per_kg: num(inp.price_per_kg), supplier_name: inp.supplier_name, origin: inp.origin, lot: inp.lot },
      outputs: rows.map((o) => ({ node_id: o.node_id, trim_level_id: o.trim_level_id || null, weight_kg: num(weights[outKey(o)]) })),
    };
    try {
      const r = runId ? await prodApi.put(`/production/runs/${runId}`, body) : await prodApi.post("/production/runs", body);
      toast.success(`Lavorazione ${r.data.number} salvata`);
      navigate(`/produzione/lavorazioni/${r.data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (loadingRun) return <Spinner text="Caricamento lavorazione..." />;

  if (!recipe) {
    const groups = {};
    recipes.forEach((r) => { (groups[r.input_node_name] ||= []).push(r); });
    return (
      <div data-testid="prod-quick-pick">
        <PTitle kicker="Reparto" title="Resa reale rapida">Scegli la lavorazione eseguita, poi registra i pesi dei tagli ottenuti.</PTitle>
        {!recipes.length && <PEmpty title="Nessuna ricetta" text="Il responsabile deve prima creare le ricette di lavorazione." />}
        {Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)).map(([g, list]) => (
          <div key={g} className="mb-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-2">{g}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {list.map((r) => (
                <button key={r.id} onClick={() => setParams({ recipe: r.id })} className="min-h-[88px] text-left bg-slate-800/70 hover:bg-slate-700 border border-slate-700 hover:border-blue-500/60 rounded-2xl p-4 transition-colors active:scale-[0.99]" data-testid={`prod-quick-recipe-${r.code}`}>
                  <p className="font-['Chivo'] font-black text-lg text-white leading-tight">{r.name}</p>
                  <p className="text-xs text-slate-400 mt-1">{r.outputs?.length} tagli · v{r.version}</p>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="pb-56 space-y-4" data-testid="prod-quick-yield">
      <PTitle kicker={runId ? "Modifica lavorazione" : "Reparto · resa reale"} title={recipe.name} right={!runId && <PButton onClick={() => setParams({})} data-testid="prod-quick-change-recipe">Cambia ricetta</PButton>}>
        Materia prima: <b className="text-slate-200">{recipe.input_node_name}</b> · ricetta v{recipe.version}
      </PTitle>

      <PCard className="space-y-3" data-testid="prod-quick-input-card">
        <PField label={`Peso in ingresso · ${recipe.input_node_name} (kg)`}>
          <PNum value={inp.total_kg} onChange={(e) => setInp({ ...inp, total_kg: e.target.value })} placeholder="0,000" className="!h-20 !text-4xl" autoFocus data-testid="prod-quick-input-peso-ingresso" />
        </PField>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <PField label="Pezzi"><PNum value={inp.pieces} onChange={(e) => setInp({ ...inp, pieces: e.target.value })} data-testid="prod-quick-pieces" /></PField>
          <PField label="Prezzo €/kg"><PNum value={inp.price_per_kg} onChange={(e) => setInp({ ...inp, price_per_kg: e.target.value })} placeholder="0,00" data-testid="prod-quick-price" /></PField>
          <PField label="Fornitore"><PInput value={inp.supplier_name} onChange={(e) => setInp({ ...inp, supplier_name: e.target.value })} placeholder="—" data-testid="prod-quick-supplier" /></PField>
          <PField label="Origine"><PInput value={inp.origin} onChange={(e) => setInp({ ...inp, origin: e.target.value })} placeholder="IT / PL / ..." data-testid="prod-quick-origin" /></PField>
          <PField label="Lotto"><PInput value={inp.lot} onChange={(e) => setInp({ ...inp, lot: e.target.value })} placeholder="—" data-testid="prod-quick-lot" /></PField>
          <PField label="Data"><PInput type="date" value={meta.date} onChange={(e) => setMeta({ ...meta, date: e.target.value })} data-testid="prod-quick-date" /></PField>
          <PField label="Operatore" className="col-span-2"><PInput value={meta.operator} onChange={(e) => setMeta({ ...meta, operator: e.target.value })} data-testid="prod-quick-operator" /></PField>
        </div>
      </PCard>

      <div className="flex items-center justify-between">
        <h2 className="font-['Chivo'] font-black text-lg text-white">Tagli ottenuti</h2>
        <PButton size="sm" onClick={() => { setAddSel({ node_id: "", trim_level_id: "" }); setAddOpen(true); }} data-testid="prod-quick-add-cut"><Plus className="w-4 h-4" /> Taglio fuori ricetta</PButton>
      </div>
      <div className="grid md:grid-cols-2 gap-2">
        {rows.map((o) => {
          const k = outKey(o);
          const kg = num(weights[k]);
          const theo = inputKg * (o.pct || 0) / 100;
          const realPct = inputKg > 0 ? (kg / inputKg) * 100 : 0;
          const diff = inputKg > 0 && kg > 0 ? realPct - (o.pct || 0) : null;
          const isSell = SELL.includes(o.disposal);
          return (
            <div key={k} className={`rounded-2xl border p-3 flex items-center gap-3 ${kg > 0 ? "bg-slate-800/80 border-slate-600" : "bg-slate-800/40 border-slate-700"}`} data-testid={`prod-cut-row-${k}`}>
              <div className="flex-1 min-w-0">
                <p className={`font-bold leading-tight ${isSell ? "text-white" : "text-slate-300"}`}>{o.node_name}{o.trim_level_name && <span className="text-slate-500 text-xs font-normal"> · {o.trim_level_name}</span>}</p>
                <p className="text-xs text-slate-400 tabular-nums mt-0.5">
                  {o.extra ? <PBadge tone="violet">fuori ricetta</PBadge> : <>teorico {fmtKg(theo, 2)} kg ({fmtPct(o.pct)})</>}
                  {diff != null && <span className={`ml-2 font-bold ${Math.abs(diff) < 0.05 ? "text-slate-300" : diff > 0 ? "text-emerald-400" : "text-amber-400"}`}>{diff > 0 ? "+" : ""}{diff.toFixed(2)} pt</span>}
                </p>
              </div>
              <div className="w-36 shrink-0"><PNum value={weights[k] ?? ""} onChange={(e) => setWeights({ ...weights, [k]: e.target.value })} placeholder="0,000" className="!h-16 !text-2xl" data-testid={`prod-cut-input-kg-${k}`} /></div>
            </div>
          );
        })}
      </div>
      <PField label="Note"><PInput value={meta.notes} onChange={(e) => setMeta({ ...meta, notes: e.target.value })} placeholder="Annotazioni sulla lavorazione" data-testid="prod-quick-notes" /></PField>

      <div className="fixed bottom-0 left-0 right-0 z-40 bg-slate-900/95 backdrop-blur-md border-t-2 border-slate-700 p-3 md:p-4" data-testid="prod-quick-footer">
        <div className="max-w-[1500px] mx-auto space-y-2">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Ingresso</p><p className="font-['Chivo'] font-black text-xl md:text-2xl text-white tabular-nums" data-testid="prod-quick-input-total">{fmtKg(inputKg, 3)}</p></div>
            <div><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Registrato</p><p className="font-['Chivo'] font-black text-xl md:text-2xl text-white tabular-nums" data-testid="prod-quick-total-registered">{fmtKg(total, 3)}</p></div>
            <div><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Differenza</p><p className={`font-['Chivo'] font-black text-xl md:text-2xl tabular-nums ${st.status === "ok" ? "text-emerald-400" : st.status === "excess" ? "text-red-400" : "text-amber-400"}`} data-testid="prod-quick-difference">{st.status === "unknown" ? "—" : fmtKg(st.diff, 3)}</p></div>
          </div>
          <ReconcileBar input={inputKg} total={total} testId="prod-quick-reconcile-bar" />
          <div className="flex gap-2">
            <PButton variant="ghost" className="flex-1" onClick={() => setWeights({})} data-testid="prod-quick-reset">Azzera pesi</PButton>
            <PButton variant={st.status === "ok" ? "success" : "primary"} className="flex-[2]" size="lg" onClick={save} disabled={busy} data-testid="prod-quick-save-btn">{runId ? "Aggiorna lavorazione" : "Salva lavorazione"}</PButton>
          </div>
        </div>
      </div>

      <PModal open={addOpen} onClose={() => setAddOpen(false)} title="Taglio fuori ricetta">
        <div className="space-y-3">
          <PField label="Taglio"><PSelect value={addSel.node_id} onChange={(e) => setAddSel({ ...addSel, node_id: e.target.value })} data-testid="prod-quick-extra-node"><option value="">— Scegli —</option>{nodes.filter((n) => n.active !== false).map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}</PSelect></PField>
          <PField label="Rifilatura"><PSelect value={addSel.trim_level_id} onChange={(e) => setAddSel({ ...addSel, trim_level_id: e.target.value })} data-testid="prod-quick-extra-trim"><option value="">—</option>{trims.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</PSelect></PField>
          <p className="text-xs text-slate-500">Il taglio entra nella lavorazione con resa teorica 0%: utile per output non previsti dalla ricetta.</p>
          <div className="flex gap-2 justify-end"><PButton onClick={() => setAddOpen(false)}>Annulla</PButton><PButton variant="primary" onClick={addExtra} disabled={!addSel.node_id} data-testid="prod-quick-extra-confirm">Aggiungi</PButton></div>
        </div>
      </PModal>
    </div>
  );
}
